import { useCallback, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { nanoid } from 'nanoid'
import { fetchDataset } from '@/actions/ts/dashboard'
import { requiredVectorMeasurements } from '@/Util/MeasurementUtils'
import { getTrialState } from '../utils'
import { getIntervalQuery } from '@/components/TrialModeView/components/MetricsCard/DateFilter/utils/dateFilterUtils'

export const useTrialData = (currentTrial, envMetrics, soilMetrics) => {
  const dispatch = useDispatch()
  const {
    envSensorsA,
    envSensorsB,
    soilSensorsA,
    soilSensorsB,
    dateFilterEnv,
    dateFilterSoil
  } = useSelector(getTrialState)

  const envMeasurementsToFetch = useMemo(() => {
    const metricIds = new Set(envMetrics.map(m => m.id))
    Object.entries(requiredVectorMeasurements).forEach(
      ([metricId, dependencies]) => {
        if (metricIds.has(metricId)) {
          metricIds.delete(metricId)
          dependencies.forEach(dep => metricIds.add(dep))
        }
      }
    )
    return Array.from(metricIds)
  }, [envMetrics])

  const soilMeasurementsToFetch = useMemo(() => {
    const metricIds = new Set(soilMetrics.map(m => m.id))
    Object.entries(requiredVectorMeasurements).forEach(
      ([metricId, dependencies]) => {
        if (metricIds.has(metricId)) {
          metricIds.delete(metricId)
          dependencies.forEach(dep => metricIds.add(dep))
        }
      }
    )
    return Array.from(metricIds)
  }, [soilMetrics])

  const fetchTrialDataForRoom = useCallback(
    (deviceType = null, roomId, sensors) => {
      if (!currentTrial || !roomId) return

      const dateFilter =
        deviceType === 'envirosense' ? dateFilterEnv : dateFilterSoil

      const toISOString = ms => new Date(ms).toISOString()

      const fromDate = dateFilter?.fromDate
        ? toISOString(dateFilter.fromDate)
        : currentTrial.startDate

      const toDate = dateFilter?.toDate
        ? toISOString(dateFilter.toDate)
        : currentTrial.endDate ??
          toISOString(Date.now() + 1 * 24 * 60 * 60 * 1000)

      const interval = dateFilter?.timeInterval
        ? getIntervalQuery(dateFilter.timeInterval)
        : '1h'

      const measurements =
        deviceType === 'envirosense'
          ? envMeasurementsToFetch
          : deviceType === 'soilsense'
          ? soilMeasurementsToFetch
          : [...envMeasurementsToFetch, ...soilMeasurementsToFetch]

      const params = {
        table: currentTrial.zone.id,
        zones: [roomId],
        originFilter: sensors?.length > 0 ? sensors : null,
        measurements,
        fromDate,
        toDate,
        interval,
        excludeIntervalAndFilter: true,
        timeZone: currentTrial.zone?.timeZone
      }

      dispatch(fetchDataset(params, nanoid(5)))
    },
    [
      currentTrial,
      envMeasurementsToFetch,
      soilMeasurementsToFetch,
      dispatch,
      dateFilterEnv,
      dateFilterSoil
    ]
  )

  const fetchTrialData = useCallback(
    (deviceType = null) => {
      if (!currentTrial) return
      if (!deviceType || deviceType === 'envirosense') {
        fetchTrialDataForRoom('envirosense', currentTrial.roomA.id, envSensorsA)
        fetchTrialDataForRoom('envirosense', currentTrial.roomB.id, envSensorsB)
      }
      if (!deviceType || deviceType === 'soilsense') {
        fetchTrialDataForRoom('soilsense', currentTrial.roomA.id, soilSensorsA)
        fetchTrialDataForRoom('soilsense', currentTrial.roomB.id, soilSensorsB)
      }
    },
    [
      currentTrial,
      fetchTrialDataForRoom,
      envSensorsA,
      envSensorsB,
      soilSensorsA,
      soilSensorsB
    ]
  )

  useEffect(() => {
    if (currentTrial) {
      fetchTrialData()
    }
  }, [currentTrial, fetchTrialData])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('envirosense', currentTrial.roomA.id, envSensorsA)
    }
  }, [currentTrial, envSensorsA, fetchTrialDataForRoom])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('envirosense', currentTrial.roomB.id, envSensorsB)
    }
  }, [currentTrial, envSensorsB, fetchTrialDataForRoom])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('soilsense', currentTrial.roomA.id, soilSensorsA)
    }
  }, [currentTrial, soilSensorsA, fetchTrialDataForRoom])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('soilsense', currentTrial.roomB.id, soilSensorsB)
    }
  }, [currentTrial, soilSensorsB, fetchTrialDataForRoom])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('envirosense', currentTrial.roomA.id, envSensorsA)
      fetchTrialDataForRoom('envirosense', currentTrial.roomB.id, envSensorsB)
    }
  }, [
    currentTrial,
    dateFilterEnv,
    fetchTrialDataForRoom,
    envSensorsA,
    envSensorsB
  ])

  useEffect(() => {
    if (currentTrial) {
      fetchTrialDataForRoom('soilsense', currentTrial.roomA.id, soilSensorsA)
      fetchTrialDataForRoom('soilsense', currentTrial.roomB.id, soilSensorsB)
    }
  }, [
    currentTrial,
    dateFilterSoil,
    fetchTrialDataForRoom,
    soilSensorsA,
    soilSensorsB
  ])

  return { fetchTrialData, fetchTrialDataForRoom }
}
